import i18n from 'i18next';
import { DEFAULT_LOCALE, LANGUAGE_CODES, SUPPORTED_LOCALES } from 'locales/constants';
import { useEffect, useMemo } from 'react';
import { buildLocalizedPath } from 'utils/routing';

/**
 * Detects the user's preferred language based on multiple sources.
 * Priority: URL hint > i18n detected > browser language > default fallback
 *
 * @param {string|null} urlHint - A potential locale code from the URL (e.g., "es", "en-CA")
 * @returns {string} The detected locale code (e.g., "en", "es")
 */
const detectPreferredLanguage = (urlHint) => {
    const detected = urlHint || i18n.language || navigator.language || DEFAULT_LOCALE;
    const detectedLower = detected.toLowerCase();

    if (detectedLower.startsWith('es')) {
        return LANGUAGE_CODES.ES;
    }
    if (detectedLower.startsWith('en')) {
        return LANGUAGE_CODES.EN;
    }

    return DEFAULT_LOCALE;
};

/**
 * Checks if a string matches the locale pattern (e.g., "en", "es-MX", "en-US")
 *
 * @param {string} segment - The URL segment to test
 * @returns {boolean} True if the segment looks like a locale code
 */
const isLocalePattern = (segment) => {
    return /^[a-z]{2}(-[a-z]{2,4})?$/i.test(segment);
};

/**
 * Removes duplicate basename segments from the path parts.
 * This handles cases where the basename might appear multiple times in the URL.
 *
 * @param {string[]} pathParts - The path segments to clean
 * @param {string} baseName - The base name to deduplicate
 * @returns {string[]} Cleaned path parts without duplicate basename
 */
const deduplicateBaseName = (pathParts, baseName) => {
    const normalizedBase = baseName.split('/').filter(Boolean);

    if (normalizedBase.length === 0) {
        return pathParts;
    }

    const startsWithBase = normalizedBase.every((part, i) => pathParts[i] === part);

    return startsWithBase ? pathParts.slice(normalizedBase.length) : pathParts;
};

/**
 * Custom hook for handling locale-based routing.
 * Automatically redirects to the appropriate locale-prefixed URL if needed.
 *
 * @returns {{
 *   isRedirecting: boolean,
 *   locale: string|null,
 *   basename: string
 * }}
 */
export const useLocaleRouting = () => {
    const { pathname, search } = window.location;
    const baseName = process.env.REACT_APP_BASE_NAME || '';

    const { isLangFound, firstSegment, pathParts } = useMemo(() => {
        const parts = pathname.split('/').filter(Boolean);
        const first = parts[0] || '';

        return {
            pathParts: parts,
            firstSegment: first,
            isLangFound: SUPPORTED_LOCALES.includes(first),
        };
    }, [pathname]);

    useEffect(() => {
        if (isLangFound) return;

        const normalizedBase = baseName.split('/').filter(Boolean);
        const startsWithBase = normalizedBase.length > 0 && normalizedBase.every((part, i) => pathParts[i] === part);

        const urlHint = !startsWithBase && isLocalePattern(firstSegment) ? firstSegment : null;
        const preferredLang = detectPreferredLanguage(urlHint);

        let cleanParts = urlHint ? pathParts.slice(1) : [...pathParts];
        cleanParts = deduplicateBaseName(cleanParts, baseName);
        const newPath = buildLocalizedPath(preferredLang, baseName, cleanParts, search);

        window.location.replace(newPath);
    }, [isLangFound, firstSegment, pathParts, baseName, search]);

    return {
        isRedirecting: !isLangFound,
        locale: isLangFound ? firstSegment : null,
        basename: isLangFound ? `/${firstSegment}${baseName === '/' ? '' : baseName}` : baseName,
    };
};
